import React, { useEffect, useState, useCallback } from 'react';
import { FileDown, Loader2, Pencil, Trash2 } from 'lucide-react';
import { getTransactions, deleteTransaction } from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import NewTransactionFormScreen from './NewTransactionFormScreen';
import FlatpickrInput from './FlatpickrInput';
import { exportTransactionsPdf } from '../utils/exportTransactionsPdf';
import { useActiveProject } from '../context/ActiveProjectContext';
import StyledSelect from './StyledSelect';

const inputCls = 'px-3 py-2 rounded-xl border text-slate-700 text-sm outline-none transition';

const formatMoney = (amount, code = 'PKR') => `${code} ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const TransactionTable = ({
  refreshSignal,
  onBalanceRefresh,
  projectFilter,
  onRowClick,
  canCreateTransactions = true,
  canEditTransactions = true,
  canDeleteTransactions = true,
  canManageTransactions = true,
  canExportTransactions = true,
}) => {
  const transactionTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'receipt', label: 'Receipt' },
    { value: 'payment', label: 'Payment' },
  ];

  const { fmt, currency } = useCurrency();
  const { selectedProject, refreshProjects } = useActiveProject();

  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    type: '',
    date_from: '',
    date_to: '',
  });

  const normalizedProjectFilter = projectFilter ? String(projectFilter) : '';
  const lockedProjectName = selectedProject?.name || '';

  useEffect(() => {
    setPage(1);
  }, [normalizedProjectFilter]);

  const perPage = 15;
  const totalPages = Math.ceil(total / perPage);

  const fetchData = useCallback(async () => {
    if (!normalizedProjectFilter) {
      setTransactions([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        ...filters,
        project_id: normalizedProjectFilter,
      };

      if (lockedProjectName) {
        params.project_name = lockedProjectName;
      }

      const res = await getTransactions(params);
      const rows = res?.data?.data?.transactions || [];

      const strictRows = rows.filter((tx) => {
        const txProjectId = tx?.project_id ?? tx?.projectId ?? tx?.project?.id;
        const txProjectName = tx?.project_name ?? tx?.project?.name;

        if (String(txProjectId) === normalizedProjectFilter) return true;

        if (
          lockedProjectName &&
          String(txProjectName || '').toLowerCase() ===
            String(lockedProjectName).toLowerCase()
        ) {
          return true;
        }

        return false;
      });

      setTransactions(strictRows);
      setTotal(strictRows.length);
    } catch (err) {
      console.error(err);
      setTransactions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters, normalizedProjectFilter, lockedProjectName]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshSignal]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction? Balances will be recalculated.')) return;
    try {
      await deleteTransaction(id);
      fetchData();
      refreshProjects?.();
      onBalanceRefresh?.();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const handleModalSaved = () => {
    fetchData();
    refreshProjects?.();
    onBalanceRefresh?.();
  };

  const openForm = (tx = null) => {
    setEditData(tx);
    setFormOpen(true);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await getTransactions({
        page: 1,
        per_page: 5000,
        ...filters,
        project_id: normalizedProjectFilter,
        ...(lockedProjectName ? { project_name: lockedProjectName } : {}),
      });

      const rows = res?.data?.data?.transactions || [];

      const filterParts = [];
      if (filters.search) filterParts.push(`Search: "${filters.search}"`);
      if (filters.type) filterParts.push(`Type: ${filters.type}`);
      if (lockedProjectName) filterParts.push(`Project: ${lockedProjectName}`);
      if (filters.date_from) filterParts.push(`From: ${filters.date_from}`);
      if (filters.date_to) filterParts.push(`To: ${filters.date_to}`);

      exportTransactionsPdf({
        rows,
        currency,
        fmt,
        title: 'Transaction Ledger',
        filePrefix: 'transaction-ledger',
        filterSummary: filterParts.join('  ·  '),
      });
    } catch (err) {
      console.error('PDF export failed', err);
      alert('PDF export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-800">Transaction Ledger</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {total} record{total !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {canExportTransactions ? (
              <button
                onClick={handleExportPDF}
                disabled={exporting || transactions.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'rgba(196,69,105,0.08)',
                  color: '#c44569',
                  border: '1.5px solid #f9b8ca',
                }}
                title="Export visible transactions to PDF"
              >
                {exporting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Exporting...
                  </>
                ) : (
                  <>
                    <FileDown size={14} /> Export PDF
                  </>
                )}
              </button>
            ) : null}

            {canCreateTransactions ? (
              <button
                onClick={() => {
                  openForm(null);
                }}
                className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl transition shadow-md"
                style={{ background: '#c44569' }}
              >
                <span className="text-lg leading-none">+</span> Add New Transaction
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 px-5 py-3 bg-slate-50 border-b border-slate-100">
          <input
            name="search"
            placeholder="Search description..."
            value={filters.search}
            onChange={handleFilterChange}
            className={`${inputCls} min-w-[180px] flex-1`}
          />

          <div className="min-w-[170px]">
            <StyledSelect
              value={filters.type}
              onChange={(next) => {
                setFilters((prev) => ({ ...prev, type: next }));
                setPage(1);
              }}
              options={transactionTypeOptions}
              placeholder="All Types"
            />
          </div>

          <FlatpickrInput
            value={filters.date_from}
            onChange={(d) => {
              setFilters((f) => ({ ...f, date_from: d }));
              setPage(1);
            }}
            placeholder="From date"
            className="!px-3 !py-2 !rounded-xl !border !border-slate-200 !bg-white !text-slate-700 !text-sm"
          />

          <FlatpickrInput
            value={filters.date_to}
            onChange={(d) => {
              setFilters((f) => ({ ...f, date_to: d }));
              setPage(1);
            }}
            placeholder="To date"
            className="!px-3 !py-2 !rounded-xl !border !border-slate-200 !bg-white !text-slate-700 !text-sm"
          />

          {normalizedProjectFilter ? (
            <div
              className="px-4 py-2 rounded-xl border text-sm font-medium"
              style={{
                borderColor: '#f9b8ca',
                background: '#fff6f8',
                color: '#c44569',
              }}
            >
              Locked to: {lockedProjectName || `Project #${normalizedProjectFilter}`}
            </div>
          ) : null}

          <button
            onClick={() => {
              setFilters({ search: '', type: '', date_from: '', date_to: '' });
              setPage(1);
            }}
            className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-100 transition font-medium"
          >
            Clear
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
            <Loader2 className="animate-spin h-5 w-5" />
            <span className="text-sm">Loading transactions...</span>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4 text-3xl">
              📋
            </div>
            <h3 className="text-slate-700 font-semibold mb-1">No transactions found</h3>
            <p className="text-slate-400 text-sm">Add your first transaction to get started.</p>
          </div>
        ) : (
          <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['#', 'Date', 'Project', 'Category', 'Description', 'Type', 'Amount', 'Balance', 'Actions'].map(
                    (h, i) => (
                      <th
                        key={i}
                        className={`px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                          i >= 6 ? 'text-right' : 'text-left'
                        } ${i === 8 ? 'text-center' : ''}`}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-50">
                {transactions.map((tx, i) => (
                  <tr
                    key={tx.id}
                    className={`transition-colors group ${onRowClick ? 'cursor-pointer hover:bg-rose-50/60' : ''}`}
                    onClick={() => {
                      if (onRowClick) onRowClick(tx);
                    }}
                  >
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {(page - 1) * perPage + i + 1}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{tx.date}</td>
                      <td className="px-4 py-3">
                        {tx.project_name ? (
                          <span
                            className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
                            style={{ background: '#fce0e8', color: '#c44569' }}
                          >
                            {tx.project_name}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {tx.category_name ? (
                          <span
                            className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
                            style={{ background: '#eef2ff', color: '#4338ca' }}
                          >
                            {tx.category_name}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700 max-w-[220px] truncate">
                        {tx.description}
                      </td>
                      <td className="px-4 py-3">
                        {tx.type === 'receipt' ? (
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
                            style={{ background: '#fef3d2', color: '#7a5200' }}
                          >
                            Receipt
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
                            style={{ background: '#fce0e8', color: '#c44569' }}
                          >
                            Payment
                          </span>
                        )}
                      </td>
                      <td
                        className="px-4 py-3 text-right font-semibold whitespace-nowrap"
                        style={{ color: tx.type === 'receipt' ? '#b08800' : '#c44569' }}
                      >
                        {tx.type === 'receipt' ? '+' : '−'}
                        {formatMoney(tx.amount, tx.currency || 'PKR')}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-800 whitespace-nowrap">{formatMoney(tx.running_balance, tx.currency || 'PKR')}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openForm(tx);
                            }}
                            disabled={!canEditTransactions}
                            className="px-2 py-1 rounded-md text-white text-xs font-semibold inline-flex items-center gap-1"
                            style={{ background: '#b08800' }}
                          >
                            <Pencil size={11} /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await handleDelete(tx.id);
                            }}
                            disabled={!canDeleteTransactions}
                            className="px-2 py-1 rounded-md text-white text-xs font-semibold inline-flex items-center gap-1"
                            style={{ background: '#c44569' }}
                          >
                            <Trash2 size={11} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-slate-100">
            {transactions.map((tx, i) => (
              <div
                key={tx.id}
                className="px-4 py-3"
                onClick={() => {
                  if (onRowClick) onRowClick(tx);
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-slate-400">#{(page - 1) * perPage + i + 1}</p>
                  <p className="text-xs text-slate-500">{tx.date}</p>
                </div>
                <p className="mt-1 text-sm font-semibold text-slate-800">{tx.description}</p>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-slate-500">{tx.project_name || 'No Project'}</span>
                  <span className="font-semibold text-slate-700">{formatMoney(tx.running_balance, tx.currency || 'PKR')}</span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: tx.type === 'receipt' ? '#b08800' : '#c44569' }}
                  >
                    {tx.type === 'receipt' ? '+' : '−'}{formatMoney(tx.amount, tx.currency || 'PKR')}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openForm(tx);
                      }}
                      disabled={!canEditTransactions}
                      className="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-white disabled:opacity-50"
                      style={{ background: '#b08800' }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await handleDelete(tx.id);
                      }}
                      disabled={!canDeleteTransactions}
                      className="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-white disabled:opacity-50"
                      style={{ background: '#c44569' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1.5 px-6 py-4 border-t border-slate-100">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              ‹ Prev
            </button>

            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-xs font-semibold transition border ${
                  page === p
                    ? 'border-transparent text-white shadow-md'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                style={page === p ? { background: '#c44569' } : {}}
              >
                {p}
              </button>
            ))}

            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Next ›
            </button>
          </div>
        )}
      </div>

      <NewTransactionFormScreen
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditData(null);
        }}
        onSaved={handleModalSaved}
        editData={editData}
        initialProjectId={normalizedProjectFilter}
        initialProjectName={lockedProjectName}
        lockProject={Boolean(normalizedProjectFilter)}
      />
    </>
  );
};

export default TransactionTable;